/**
 * Where a Song's playhead is, honouring each section's end rule. PURE - no signals, no clock - so every
 * on-screen playhead (the v2 editor, the Legacy editor, the Timeline Launcher) computes it the same way
 * OPBridge does, and it can be tested on its own.
 *
 * DEC-227: sections loop until the performer moves on. OPBridge plays it behind
 * `/health.capabilities.timeline_section_loop` (S311, asked 2026-10-09 in `OPApps/opbridge-opbuttons.md`):
 *   - `loop`: at the section's end the playhead returns to the section's start, until a jump moves on;
 *   - `stop`: the song holds just inside the section's end (OPBridge keeps naming that section);
 *   - `auto-advance` or absent: runs into the next section, as before.
 * Past the last section the whole timeline wraps when it loops, else the run has ended.
 *
 * OPBridge's edge rule, matched here: a position exactly ON an end line belongs to what comes after it.
 * A jump is not modelled here - the caller re-anchors (`fromBar` = the section's start, elapsed from the
 * jump's bar line), which is how all three playheads already handled jumps.
 */
import { timelinePlaySections, type Timeline } from '@opbuttons/schema';

export interface PlayheadAt {
  /** Bars from the timeline's start. */
  pos: number;
  /** Holding at a `stop` section's end. */
  held: boolean;
  /** A non-looping run went past its last section. */
  ended: boolean;
}

const HOLD_INSET = 1e-6;

export function playheadAt(t: Timeline, fromBar: number, elapsedBars: number, loops: boolean): PlayheadAt {
  // Lengths as the compile sends them (`bridgeSync.ts`: at least 1 bar, whole bars), a lead-in first when there is one.
  const secs = timelinePlaySections(t);
  const lens = secs.map((s) => Math.max(1, s.lengthBars | 0));
  const total = lens.reduce((a, b) => a + b, 0);
  if (total <= 0 || lens.length === 0) return { pos: 0, held: false, ended: true };
  let pos = Math.max(0, fromBar);
  let left = Math.max(0, elapsedBars);
  if (pos >= total) {
    if (!loops) return { pos: total, held: false, ended: true };
    pos = pos % total;
  }
  // The section holding `pos` (a position on an end line is in the NEXT section).
  let k = 0; let start = 0;
  while (k < lens.length - 1 && pos >= start + lens[k]!) { start += lens[k]!; k++; }
  // Each pass either returns or moves to the next section; after a whole-timeline wrap `left` is under
  // one lap, so this ends within two laps of sections.
  for (let guard = 0; guard < lens.length * 2 + 2; guard++) {
    const len = lens[k]!; const end = start + len;
    if (pos + left < end) return { pos: pos + left, held: false, ended: false };
    left -= end - pos;
    const onEnd = secs[k]!.onEnd ?? 'auto-advance';
    if (onEnd === 'loop') return { pos: start + (left % len), held: false, ended: false };
    if (onEnd === 'stop') return { pos: end - HOLD_INSET, held: true, ended: false };
    pos = end; start = end; k++;
    if (k >= lens.length) {
      if (!loops) return { pos: total, held: false, ended: true };
      k = 0; start = 0; pos = 0; left %= total;
    }
  }
  return { pos, held: false, ended: false };
}
